import { useState } from 'react';

const terminalLines = [
  { type: 'prompt', text: 'root@cyberlab:~# nmap -sV -p 80,443,22 192.168.1.1' },
  { type: 'output', text: 'Starting Nmap 7.94 ( https://nmap.org )' },
  { type: 'output', text: 'Nmap scan report for 192.168.1.1' },
  { type: 'output', text: 'Host is up (0.0012s latency).' },
  { type: 'output', text: '' },
  { type: 'output', text: 'PORT    STATE SERVICE  VERSION' },
  { type: 'success', text: '22/tcp  open  ssh      OpenSSH 8.2p1' },
  { type: 'success', text: '80/tcp  open  http     Apache httpd 2.4.41' },
  { type: 'warning', text: '443/tcp open  ssl/http Apache httpd 2.4.41' },
  { type: 'output', text: '' },
  { type: 'success', text: 'Nmap done: 1 IP address (1 host up) scanned in 2.34s' },
  { type: 'prompt', text: 'root@cyberlab:~# _' },
];

const lineColor: Record<string, string> = {
  prompt: 'text-[#00D9FF]',
  output: 'text-gray-400',
  success: 'text-emerald-400',
  warning: 'text-amber-400',
};

const labFeatures = [
  { icon: 'ri-server-line', text: 'Isolated virtual machines — no local setup needed' },
  { icon: 'ri-shield-check-line', text: 'Safe sandboxed environment for real attack simulation' },
  { icon: 'ri-flashlight-line', text: 'Instant feedback and guided hints system' },
  { icon: 'ri-time-line', text: 'Labs available 24/7 — learn at your own pace' },
  { icon: 'ri-bar-chart-line', text: 'Progress tracking and skill assessment built-in' },
];

const labCategories = [
  { label: 'Web Exploitation', count: 120, icon: 'ri-global-line' },
  { label: 'Network Security', count: 85, icon: 'ri-wifi-line' },
  { label: 'Reverse Engineering', count: 64, icon: 'ri-code-s-slash-line' },
  { label: 'Forensics', count: 47, icon: 'ri-search-eye-line' },
  { label: 'Cryptography', count: 38, icon: 'ri-lock-line' },
  { label: 'OSINT', count: 29, icon: 'ri-eye-line' },
];

const InteractiveLabsSection = () => {
  const [activeCategory, setActiveCategory] = useState('Web Exploitation');

  return (
    <section id="interactive-labs" className="py-24 bg-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#00D9FF] text-xs font-bold tracking-widest uppercase mb-3">
            Hands-On Experience
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-4">
            Interactive Labs
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            No setup, no downloads. Launch a fully configured lab environment in seconds and start hacking right in your browser.
          </p>
        </div>

        {/* Main Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16">
          {/* Terminal Mockup */}
          <div className="rounded-2xl overflow-hidden bg-[#0D1B2A] border border-white/10">
            {/* Terminal Header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-[#0A1628] border-b border-white/10">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="w-3 h-3 rounded-full bg-amber-400"></span>
              <span className="w-3 h-3 rounded-full bg-emerald-400"></span>
              <span className="ml-4 text-gray-500 text-xs font-mono">cyberlab — bash — 80x24</span>
            </div>
            {/* Terminal Body */}
            <div className="p-5 font-mono text-sm leading-relaxed min-h-[280px]">
              {terminalLines.map((line, i) => (
                <div key={i} className={`${lineColor[line.type]} ${line.text === '' ? 'h-3' : ''}`}>
                  {line.text}
                </div>
              ))}
            </div>
            {/* Terminal Footer */}
            <div className="px-5 py-3 bg-[#0A1628] border-t border-white/10 flex items-center justify-between">
              <span className="text-gray-500 text-xs">Lab: Network Recon — Beginner</span>
              <button className="px-4 py-1.5 rounded-full bg-[#00D9FF] text-[#0A1628] text-xs font-bold hover:bg-white transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Launch Lab
              </button>
            </div>
          </div>

          {/* Right Content */}
          <div>
            <span className="inline-block text-[#00D9FF] text-xs font-bold tracking-widest uppercase mb-4">
              Interactive Experience
            </span>
            <h3 className="text-3xl md:text-4xl font-black text-[#0A1628] mb-5 leading-tight">
              Practice in Real
              <br />
              Environments
            </h3>
            <p className="text-gray-500 text-base leading-relaxed mb-7">
              Our browser-based lab environments give you access to real Linux machines, vulnerable web apps, and network simulations — all isolated and safe. No VM setup, no configuration headaches.
            </p>
            <ul className="space-y-3 mb-8">
              {labFeatures.map((f) => (
                <li key={f.text} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#00D9FF]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${f.icon} text-[#00D9FF] text-sm`}></i>
                  </div>
                  <span className="text-gray-600 text-sm leading-relaxed">{f.text}</span>
                </li>
              ))}
            </ul>
            <a
              href="#highlighted-labs"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#00D9FF] to-[#0066CC] text-white font-bold text-sm hover:opacity-90 transition-opacity duration-200 cursor-pointer whitespace-nowrap"
            >
              Try Interactive Lab <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>

        {/* Lab Categories */}
        <div className="bg-white rounded-2xl p-8 border border-gray-100">
          <h3 className="text-[#0A1628] font-black text-xl mb-6">Browse by Category</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {labCategories.map((cat) => (
              <button
                key={cat.label}
                onClick={() => setActiveCategory(cat.label)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.label
                    ? 'border-[#00D9FF] bg-[#00D9FF]/5'
                    : 'border-gray-100 hover:border-[#00D9FF]/40'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  activeCategory === cat.label ? 'bg-[#00D9FF]/10' : 'bg-gray-50'
                }`}>
                  <i className={`${cat.icon} text-lg ${activeCategory === cat.label ? 'text-[#00D9FF]' : 'text-gray-400'}`}></i>
                </div>
                <span className={`text-xs font-semibold text-center leading-tight ${
                  activeCategory === cat.label ? 'text-[#0A1628]' : 'text-gray-500'
                }`}>
                  {cat.label}
                </span>
                <span className={`text-xs ${activeCategory === cat.label ? 'text-[#00D9FF]' : 'text-gray-400'}`}>
                  {cat.count} labs
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveLabsSection;
